import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DateTime } from 'luxon';

// Spiegelt die Validierung der Bewerber-Value-Objects im Backend (Name, Strasse, HausNummer, Postleitzahl, OrtName,
// TelefonNummer, GeburtDatum). Regexe und Fehlertexte stammen aus jakarta-bean-custom-validation-lib und müssen bei
// einem Update der Library abgeglichen werden.
// Die Validatoren liefern den Fehlertext als Wert des Fehlers, damit app-field-error ihn anzeigen kann.
const PERSON_NAME_REGEX: RegExp = /^(?=.{2,80}$)[\p{L}\p{M}.'’-]+(?: [\p{L}\p{M}.'’-]+)*$/u;
const STRASSE_NAME_REGEX: RegExp = /^(?!.* {2})(?=.{1,60}$)[\p{L}\p{M}0-9](?:[\p{L}\p{M}0-9'.\- ]*[\p{L}\p{M}0-9]\.?)?$/u;
const HAUSNUMMER_REGEX: RegExp = /^(?!.* {2})(?=.{1,20}$)[\p{L}\p{M}0-9](?:[\p{L}\p{M}0-9'.\- ]*[\p{L}\p{M}0-9])?$/u;
const POSTLEITZAHL_REGEX: RegExp = /^(?=.{2,10}$)[A-Za-z0-9]+(?:[ -][A-Za-z0-9]+)*$/;
const ORT_NAME_REGEX: RegExp = /^(?!.* {2})(?=.{1,100}$)[\p{L}\p{M}](?:[\p{L}\p{M}',.\-() ]*(?:[\p{L}\p{M}]|[\p{L}\p{M}]\)))?$/u;
const EMAIL_ADRESSE_REGEX: RegExp =
  /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;
const FESTNETZ_TELEFON_NUMMER_REGEX: RegExp = /^([+0])\d+[\d /\-.]*\d+$/;

const TELEFON_NUMMER_MIN_LENGTH: number = 5;
const TELEFON_NUMMER_MAX_LENGTH: number = 20;
const EARLIEST_GEBURTSDATUM: DateTime = DateTime.fromISO('1900-01-01');

const PERSON_NAME_MESSAGE: string =
  'Invalider Name. Erlaubt sind Texte mit 2 bis 80 Zeichen, die nur aus Buchstaben aller Sprachen, Akzent-/Kombinationszeichen, ' +
  'Leerzeichen, Punkt, Apostroph (normal oder typografisch) und Bindestrich bestehen.';
const STRASSE_NAME_MESSAGE: string =
  'Invalider Straßenname. Ein Straßenname erlaubt 1 bis 60 Zeichen, beginnt mit einem Buchstaben oder einer Ziffer und endet mit einem ' +
  'Buchstaben, einer Ziffer oder einem Punkt und erlaubt dazwischen zusätzlich Punkte, Apostrophe, Bindestriche oder einzelne ' +
  'Leerzeichen.';
const HAUSNUMMER_MESSAGE: string =
  'Invalide Hausnummer. Eine Hausnummer erlaubt 1 bis 20 Zeichen, beginnt und endet mit einem Buchstaben oder einer Ziffer ' +
  'und erlaubt dazwischen zusätzlich Punkte, Apostrophe, Bindestriche oder einzelne Leerzeichen.';
const POSTLEITZAHL_MESSAGE: string =
  'Invalide Postleitzahl. Eine Postleitzahl erlaubt 2 bis 10 Zeichen, beginnt und endet mit einem Buchstaben oder einer Ziffer ' +
  'und erlaubt dazwischen zusätzlich Bindestriche oder einzelne Leerzeichen.';
// Der Text der Library nennt Ziffern als erlaubt und verschweigt Kommas, ORT_NAME_REGEX sieht das umgekehrt vor.
// Angezeigt wird daher eine korrigierte Fassung.
const ORT_NAME_MESSAGE: string =
  'Invalider Ortsname. Ein Ortsname erlaubt 1 bis 100 Zeichen, beginnt mit einem Buchstaben, endet mit einem Buchstaben ' +
  'oder einer schließenden Klammer und erlaubt dazwischen zusätzlich Punkte, Kommas, Apostrophe, Bindestriche, Klammern ' +
  'oder einzelne Leerzeichen.';
const TELEFON_NUMMER_MESSAGE: string = `Die Telefonnummer muss zwischen ${TELEFON_NUMMER_MIN_LENGTH} und ${TELEFON_NUMMER_MAX_LENGTH} Zeichen lang sein.`;
const FESTNETZ_TELEFON_NUMMER_MESSAGE: string =
  'Die Telefonnummer muss mit + oder 0 beginnen und darf nur Ziffern, Leerzeichen, Schrägstriche, Bindestriche und Punkte enthalten.';
const EMAIL_ADRESSE_MESSAGE: string = 'Die E-Mail-Adresse muss korrekt formatiert sein.';
const GEBURTSDATUM_TOO_EARLY_MESSAGE: string = 'Das Geburtsdatum darf nicht vor dem 01.01.1900 liegen.';
const GEBURTSDATUM_IN_FUTURE_MESSAGE: string = 'Das Geburtsdatum darf nicht in der Zukunft liegen.';

// Das Backend normalisiert die Werte vor der Validierung (Strings.removeControlCharacters und
// Strings.trimAndNormalizeWhitespaces), daher hier dieselbe Normalisierung.
function normalize(value: string): string {
  return value
    .replace(/[\b\t\n\v\f\r\u007F]/g, '')
    .trim()
    .replace(/\s+/g, ' ');
}

// Leere Werte gelten wie bei den Angular-Validatoren als gültig: Leere optionale Felder werden im Backend zu null,
// Pflichtfelder sichert Validators.required ab.
function normalizedPatternValidator(regex: RegExp, errorKey: string, message: string): ValidatorFn {
  return (control: AbstractControl<string | null>): ValidationErrors | null => {
    if (control.value === null) {
      return null;
    }
    const value: string = normalize(control.value);
    if (value === '' || regex.test(value)) {
      return null;
    }
    return { [errorKey]: message };
  };
}

export const personNameValidator: ValidatorFn = normalizedPatternValidator(PERSON_NAME_REGEX, 'personName', PERSON_NAME_MESSAGE);
export const strasseNameValidator: ValidatorFn = normalizedPatternValidator(STRASSE_NAME_REGEX, 'strasseName', STRASSE_NAME_MESSAGE);
export const hausNummerValidator: ValidatorFn = normalizedPatternValidator(HAUSNUMMER_REGEX, 'hausNummer', HAUSNUMMER_MESSAGE);
export const postleitzahlValidator: ValidatorFn = normalizedPatternValidator(POSTLEITZAHL_REGEX, 'postleitzahl', POSTLEITZAHL_MESSAGE);
export const ortNameValidator: ValidatorFn = normalizedPatternValidator(ORT_NAME_REGEX, 'ortName', ORT_NAME_MESSAGE);
// Bewusst strenger als @Email im Backend, das z. B. auch Adressen ohne Top-Level-Domain wie name@localhost akzeptiert.
export const emailAdresseValidator: ValidatorFn = normalizedPatternValidator(EMAIL_ADRESSE_REGEX, 'emailAdresse', EMAIL_ADRESSE_MESSAGE);
// Die Festnetznummer kennt die Bewerber-Entity nicht, sie geht nur ins CRM. Daher keine Backend-Regel als Vorlage.
export const festnetzTelefonNummerValidator: ValidatorFn = normalizedPatternValidator(
  FESTNETZ_TELEFON_NUMMER_REGEX,
  'festnetzTelefonNummer',
  FESTNETZ_TELEFON_NUMMER_MESSAGE
);

export function telefonNummerValidator(control: AbstractControl<string | null>): ValidationErrors | null {
  if (control.value === null) {
    return null;
  }
  // TelefonNummer.normalize ersetzt zusätzlich Trennzeichen durch Leerzeichen, bevor die Länge geprüft wird.
  const value: string = normalize(control.value.replace(/[/\-−()]/g, ' '));
  if (value === '' || (value.length >= TELEFON_NUMMER_MIN_LENGTH && value.length <= TELEFON_NUMMER_MAX_LENGTH)) {
    return null;
  }
  return { telefonNummer: TELEFON_NUMMER_MESSAGE };
}

export function geburtsdatumValidator(control: AbstractControl<Date | null>): ValidationErrors | null {
  if (control.value === null) {
    return null;
  }
  const geburtsdatum: DateTime = DateTime.fromJSDate(control.value).startOf('day');
  if (geburtsdatum < EARLIEST_GEBURTSDATUM) {
    return { geburtsdatumTooEarly: GEBURTSDATUM_TOO_EARLY_MESSAGE };
  }
  if (geburtsdatum > DateTime.now().startOf('day')) {
    return { geburtsdatumInFuture: GEBURTSDATUM_IN_FUTURE_MESSAGE };
  }
  return null;
}
